// BiddingBlockApprovalRequest[] → BlockBidRow[] 変換。
//
// BFF GetBiddingBlockApprovalRequest (N+1 並列 Get) の結果から入札一覧テーブル行を組み立てる。
// 約定結果テーブル行 (BlockExecResult[]) とポジション chart slot (PositionChartSlot[]) の
// 導出ヘルパーも提供する。 いずれも純関数 (= hook / 副作用なし)。
import { timestampDate } from '@bufbuild/protobuf/wkt'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import type { BiddingBlockApprovalRequest } from '@ebalance/proto/bffuser/v1/bidding_block_model_pb'
import {
  BiddingBlockBidGroupType,
  BiddingBlockBidStatus,
  BiddingBlockBidType,
} from '@ebalance/proto/bffuser/v1/bidding_block_model_pb'

import { resolveRemainMw } from '~/components/bidding/common/coverageColors'
import {
  addToKomaCoverLines,
  createKomaCoverLines,
  readKomaLine,
} from '~/components/bidding/common/positionLineTotals'
import type { PositionChartSlot } from '~/components/bidding/common/types'
import { decimalToNumber, decimalToNumberOr } from '~/models/decimal'
import { komaEndTime, komaNumberToTime } from '~/models/komaToTime'
import { unknownIssuerBidMw } from '~/models/unknownIssuerBid'

import { UNKNOWN_ISSUER_COUNTED_CATEGORIES } from './bidStatusCategory'
import { type BlockBidResult, toBlockBidResult } from './blockBidResult'
import {
  type BlockBidRow,
  type BlockExecChildRow,
  type BlockExecKind,
  type BlockExecResult,
  type BlockKind,
  isUnknownIssuerBlockRow,
} from './blockBidRow'
import { deriveBlockBidStatusCategory } from './blockBidStatusCategory'
import { resolveBlockBidGroupWithdrawState } from './blockBidWithdrawAction'
import { isExpiredBlockGroup } from './blockDraftGroup'
import { coverLineOfSpotCategory } from './spotBidStatusCategory'

// ---------- private helpers ----------

function blockBidTypeToKind(t: BiddingBlockBidType): BlockKind {
  switch (t) {
    case BiddingBlockBidType.BLOCK:
      return 'STANDARD'
    case BiddingBlockBidType.LINK_BLOCK_P:
      return 'LINK-P'
    case BiddingBlockBidType.LINK_BLOCK_C:
      return 'LINK-C'
    case BiddingBlockBidType.LOOP_BLOCK_A:
      return 'LOOP-A'
    case BiddingBlockBidType.LOOP_BLOCK_B:
      return 'LOOP-B'
    default:
      return 'STANDARD'
  }
}

/**
 * ブロック種別の表示名 i18n key。 model 層は t() を持てないので key を返し、
 * 描画側 ([[formatBlockBidLegSummary]]) で解決する (#1114)。
 */
function blockBidTypeNameKey(type: BiddingBlockBidType): string {
  switch (type) {
    case BiddingBlockBidType.LINK_BLOCK_P:
      return 'page:biddingSpot.blockBidType.linkParent'
    case BiddingBlockBidType.LINK_BLOCK_C:
      return 'page:biddingSpot.blockBidType.linkChild'
    case BiddingBlockBidType.LOOP_BLOCK_A:
      return 'page:biddingSpot.blockBidType.loopA'
    case BiddingBlockBidType.LOOP_BLOCK_B:
      return 'page:biddingSpot.blockBidType.loopB'
    case BiddingBlockBidType.BLOCK:
    default:
      return 'page:biddingSpot.blockBidType.block'
  }
}

/** slot 番号 (1-origin) の終了時刻ラベル "HH:MM"。 slot 48 は "24:00"。 */
function slotEndTime(slotIndex: number): string {
  return komaEndTime(komaNumberToTime(slotIndex))
}

/**
 * timeSlots の timeCode 配列から "コマ X〜Y (HH:MM〜HH:MM)" ラベルを生成する。
 * timeCode は 1-origin (JEPX 規約と一致)。
 */
function buildKomaRangeLabel(timeCodes: number[]): string {
  if (timeCodes.length === 0) return '—'
  const sorted = [...timeCodes].sort((a, b) => a - b)
  const min = sorted[0] ?? 1
  const max = sorted[sorted.length - 1] ?? 1
  return `コマ ${min}〜${max} (${komaNumberToTime(min)}〜${slotEndTime(max)})`
}

const EXEC_STATUS_BY_RESULT: Record<
  BlockBidResult,
  Pick<BlockExecResult, 'statusLabelKey' | 'statusBadge'>
> = {
  full: {
    statusLabelKey: 'page:biddingSpot.blockExecStatus.matched',
    statusBadge: 'positive',
  },
  none: {
    statusLabelKey: 'page:biddingSpot.blockExecStatus.unmatched',
    statusBadge: 'neutral',
  },
  expired: {
    statusLabelKey: 'page:biddingSpot.bidStatusCategory.expired',
    statusBadge: 'negative',
  },
  cancelled: {
    statusLabelKey: 'page:biddingSpot.blockExecStatus.cancelled',
    statusBadge: 'neutral',
  },
}

function groupTypeToExecKind(g: BiddingBlockBidGroupType): BlockExecKind {
  switch (g) {
    case BiddingBlockBidGroupType.LINK_BLOCK:
      return 'LINK'
    case BiddingBlockBidGroupType.LOOP_BLOCK:
      return 'LOOP'
    default:
      return 'STANDARD'
  }
}

// ---------- public functions ----------

/**
 * GetBiddingBlockApprovalRequest の並列結果 (group 配列) を入札一覧テーブル行に展開する。
 *
 * - 1 group (BiddingBlockApprovalRequest) が 1〜2 leg (BiddingBlockBid) を含む。
 * - BLOCK (通常) は leg 1 本、 LINK_BLOCK/LOOP_BLOCK は leg 2 本。
 * - leg を 1 行として展開するため、 2 leg グループは 2 行になる。
 */
export function convertBlockBidRows(
  requests: BiddingBlockApprovalRequest[],
): BlockBidRow[] {
  const rows: BlockBidRow[] = []
  for (const req of requests) {
    const isPaired =
      req.groupType === BiddingBlockBidGroupType.LINK_BLOCK ||
      req.groupType === BiddingBlockBidGroupType.LOOP_BLOCK
    // primary → secondary の順で行を積む (= 親→子の表示順)。
    const bids = [req.primary, req.secondary].filter(
      (b): b is NonNullable<typeof b> => b !== undefined,
    )
    // 取下の進み具合はグループの全 leg から決め、 表示フィルタで leg が欠けても変えない。
    const withdrawState = resolveBlockBidGroupWithdrawState(
      bids.map((b) => b.bidStatus),
    )
    const groupExpired = isExpiredBlockGroup(req)
    for (const bid of bids) {
      const timeCodes = bid.timeSlots.map((s) => s.timeCode)
      // slotCodes と index 対応する約定量 (= time_slots.contract_volume_mwh)。
      // 未約定 slot は 0。 ポジション chart の executedMw 実データ化に使う。
      const slotExecVolumes = bid.timeSlots.map((s) =>
        decimalToNumberOr(s.contractVolumeMwh, 0),
      )
      const slotExecPrices = bid.timeSlots.map((s) =>
        decimalToNumber(s.contractPriceYenPerKwh),
      )
      // slotCodes と index 対応するコマ別入札量 (AC-BSP-030a: コマ毎に異なり得る)。
      const slotVolumes = bid.timeSlots.map((s) =>
        decimalToNumberOr(s.volumeMwh, 0),
      )
      rows.push({
        id: bid.id,
        groupId: req.id,
        groupType: req.groupType,
        kind: blockBidTypeToKind(bid.blockBidType),
        nameKey: blockBidTypeNameKey(bid.blockBidType),
        areaCode: bid.areaCode,
        direction: bid.direction,
        komaRangeLabel: buildKomaRangeLabel(timeCodes),
        volumeMwh: slotVolumes[0] ?? 0,
        priceYenPerKwh: decimalToNumberOr(bid.priceYenPerKwh, 0),
        bidStatus: bid.bidStatus,
        needsReview: req.needsReview,
        needsReviewSince:
          req.needsReviewSince !== undefined
            ? timestampDate(req.needsReviewSince)
            : undefined,
        targetDate:
          req.targetDate !== undefined
            ? new Date(
                req.targetDate.year,
                req.targetDate.month - 1,
                req.targetDate.day,
              )
            : undefined,
        deliveryContractCode: bid.deliveryContractCode,
        memoJepx: bid.memoJepx,
        memoFree: bid.memoFree,
        bidNo: bid.jepxBidNo,
        createdBy: req.createdBy,
        createdAt:
          req.createdAt !== undefined
            ? timestampDate(req.createdAt)
            : new Date(0),
        approvalState: req.approvalState,
        sendStatus: req.sendStatus,
        sendErrorCategory: req.sendErrorCategory,
        sendErrorDetail: req.sendErrorDetail,
        receiptChecking: req.receiptChecking,
        slotCodes: timeCodes,
        slotExecVolumes,
        slotExecPrices,
        slotVolumes,
        pairId: isPaired ? req.id : undefined,
        isChild:
          bid.blockBidType === BiddingBlockBidType.LINK_BLOCK_C ||
          bid.blockBidType === BiddingBlockBidType.LOOP_BLOCK_B
            ? true
            : undefined,
        issuerType: req.issuerType,
        withdrawable: req.withdrawable,
        returnableToDraft: req.returnableToDraft,
        workflowId: req.workflowId,
        groupExpired,
        withdrawState,
      })
    }
  }
  return rows
}

const BLOCK_EXEC_ROLE_KEY: Record<BlockKind, string | undefined> = {
  STANDARD: undefined,
  'LINK-P': 'page:biddingSpot.blockExec.role.linkParent',
  'LINK-C': 'page:biddingSpot.blockExec.role.linkChild',
  'LOOP-A': 'page:biddingSpot.blockExec.role.loopA',
  'LOOP-B': 'page:biddingSpot.blockExec.role.loopB',
}

const pad2 = (n: number): string => String(n).padStart(2, '0')

/** Leg のコマ範囲 (= "09〜16")。 */
function legKomaRange(row: BlockBidRow): string {
  const codes = [...row.slotCodes].sort((a, b) => a - b)
  return `${pad2(codes[0] ?? 1)}〜${pad2(codes[codes.length - 1] ?? 1)}`
}

/**
 * BlockBidRow[] から約定結果テーブル行 (BlockExecResult[]) を導出する (AC-BSP-037)。
 *
 * rows を groupId でまとめ、 1 group = 1 親行として出力する。 親行のエリアとコマ範囲は全 Leg の
 * 値をつなぎ、 子テーブルは Leg × コマ を 1 行にする (design `#block-exec-table`)。 Leg は
 * 先頭の Leg (STANDARD / LINK-P / LOOP-A) から並べる。
 */
export function convertBlockExecResults(
  rows: BlockBidRow[],
): BlockExecResult[] {
  // groupId ごとに rows を集約する。 insertion order を維持して一貫性を保つ。
  const groupMap = new Map<string, BlockBidRow[]>()
  for (const row of rows) {
    const group = groupMap.get(row.groupId) ?? []
    group.push(row)
    groupMap.set(row.groupId, group)
  }

  const results: BlockExecResult[] = []
  for (const [groupId, groupRows] of groupMap) {
    const legs = [...groupRows].sort(
      (a, b) => Number(a.isChild === true) - Number(b.isChild === true),
    )
    const primary = legs[0]
    if (primary === undefined) continue

    const children: BlockExecChildRow[] = legs.flatMap((leg) =>
      leg.slotCodes
        .map((koma, i) => ({
          legRoleKey: BLOCK_EXEC_ROLE_KEY[leg.kind],
          koma,
          areaCode: leg.areaCode,
          direction: leg.direction,
          bidVolumeMw: leg.slotVolumes[i] ?? leg.volumeMwh,
          // all-or-nothing のため全約定の Leg は各コマの入札量が約定量 (AC-BSP-002a)
          execVolumeMw:
            leg.bidStatus === BiddingBlockBidStatus.MATCHED
              ? (leg.slotVolumes[i] ?? leg.volumeMwh)
              : (leg.slotExecVolumes[i] ?? 0),
          execPriceYenPerKwh: leg.slotExecPrices[i],
        }))
        .sort((a, b) => a.koma - b.koma),
    )

    results.push({
      id: groupId,
      kind: groupTypeToExecKind(primary.groupType),
      areaCodes: [...new Set(legs.map((leg) => leg.areaCode))],
      komaRangeLabel: legs.map(legKomaRange).join(' + '),
      deliveryContractCode: primary.deliveryContractCode,
      ...EXEC_STATUS_BY_RESULT[toBlockBidResult(primary.bidStatus)],
      children,
    })
  }
  return results
}

/**
 * BlockBidRow[] (エリアフィルタ適用済) から 48-slot ポジション chart データを構築する。
 *
 * - coverMw (「全札（下書き含む）」線) = 各行の slotCodes × slotVolumes (コマ別入札量) 積算の
 *   **net 充当** (= 買 − 売、 入札後カバー量)。 取消・失効の leg は除く (AC-BSP-108)。
 *   コマ別 MW (net にする理由は `buildPositionSlots` の godoc / #2533)。
 * - coverSubmittedMw (「入札済み」線) = coverMw の部分集合 (`coverLineOfSpotCategory` が
 *   'submitted' と判定した leg のみ、 AC-BSP-108)。 区分はステータス列の 11 区分導出
 *   (`deriveBlockBidStatusCategory`) に委ねる。 ブロックは all-or-nothing で部分約定が無い。
 * - executedMw = 各行の slotExecVolumes (= time_slots.contract_volume_mwh) 積算の net (買 − 売)。 同上。
 * - needMw = backend GetProcurementNeed 由来の RPC 実データ (needByKoma[i]、 MW)。取得エラー時は
 *   needByKoma=undefined を渡し need=0 とする (設計書 bidding-position-need.md §7 採番、
 *   恒久 spec 未反映)。
 *
 * cover / executed / need はいずれもコマ別 MW なので、 同一 `PositionChartSlot` (MW 契約)
 * にそのまま載せる (issue #4285。 = buildPositionSlots と同じ規則)。
 *
 * 発注主体区分が空欄のグループの leg は cover にも executed にも入れず `unknownIssuerMw` に数える
 * (AC-BSP-127、 #10084。 ブロックは部分約定が無いので入札した時点の量)。
 *
 * @param isPastDeadline - 入札締切を過ぎているか (= `deriveBlockBidStatusCategory` の代理失効判定に使う)。
 */
export function buildBlockPositionSlots(
  rows: BlockBidRow[],
  needByKoma: readonly number[] | undefined,
  isPastDeadline: boolean,
): PositionChartSlot[] {
  const lines = createKomaCoverLines()
  const unknownIssuerBySlot = new Map<number, number>()
  const execBuyBySlot = new Map<number, number>()
  const execSellBySlot = new Map<number, number>()
  for (const row of rows) {
    const category = deriveBlockBidStatusCategory(row, isPastDeadline)
    const coverLine = coverLineOfSpotCategory(category)
    // 取下が成立した (AC-BSP-081) leg・取消・失効は板から下りているか約定対象外なので、
    // どちらの線にも入れない (AC-BSP-108)。
    if (coverLine === 'excluded') continue
    const isSell = row.direction === Side.SELL
    const isUnknownIssuer = isUnknownIssuerBlockRow(row)
    const countsUnknownIssuer =
      isUnknownIssuer && UNKNOWN_ISSUER_COUNTED_CATEGORIES.has(category)
    const execTarget = isSell ? execSellBySlot : execBuyBySlot
    for (const [i, code] of row.slotCodes.entries()) {
      // コマ別入札量を使う (AC-BSP-030a。 代表値を全コマに掛けると
      // コマ毎に量が違う入札でカバー量が実態とズレる)。
      const vol = row.slotVolumes[i] ?? row.volumeMwh
      const execVol = row.slotExecVolumes[i] ?? 0
      addToKomaCoverLines(lines, coverLine, {
        komaKey: code,
        isSell,
        volume: vol,
        countsToCover: !isUnknownIssuer,
      })
      if (isUnknownIssuer) {
        const unknownMw = unknownIssuerBidMw({
          counted: countsUnknownIssuer,
          direction: row.direction,
          mw: vol,
        })
        if (unknownMw !== undefined) {
          unknownIssuerBySlot.set(
            code,
            (unknownIssuerBySlot.get(code) ?? 0) + unknownMw,
          )
        }
        continue
      }
      execTarget.set(code, (execTarget.get(code) ?? 0) + execVol)
    }
  }
  return Array.from({ length: 48 }, (_, i) => {
    const index = i + 1
    // cover / executed / need はいずれもコマ別 MW (issue #4285)。
    const all = readKomaLine(lines.all, index)
    const submitted = readKomaLine(lines.submitted, index)
    const need = needByKoma?.[i] ?? 0
    return {
      index,
      coverMw: all.coverMw,
      coverSubmittedMw: submitted.coverMw,
      needMw: need,
      remainMw: resolveRemainMw(all.coverMw, need),
      executedMw:
        (execBuyBySlot.get(index) ?? 0) - (execSellBySlot.get(index) ?? 0),
      unknownIssuerMw: unknownIssuerBySlot.get(index),
      buyMw: all.buyMw,
      sellMw: all.sellMw,
      buySubmittedMw: submitted.buyMw,
      sellSubmittedMw: submitted.sellMw,
    }
  })
}
